import { CircularProgress, ThemeProvider } from "@mui/material";
import { useEffect, useState } from "react";
import { useFormik } from "formik";
import { useMutation, useQuery } from "@tanstack/react-query";

import { getBank } from "../../../../services/paymentApi";
import {
  checkStatusByEmail,
  getBusinessField,
  registerNonMpi,
} from "../../../../services/authApi";
import useDebounce from "../../../hooks/useDebounce";
import { getRole, getTitle } from "../../../../utils/FetchData";
import { theme } from "../../../constants/muiTheme";
import LoadingBackdrop from "../../../common/particles.jsx/LoadingBackdrop";
import Logo from "../../../../assets/icons/logoOlin.png";
import RegisterPharmacyStepper from "./RegisterPharmacyStepper";
import AddRegisterSarana from "./RegisterPharmacyComponent/AddRegisterSarana";
import AddRegisterPemilik from "./RegisterPharmacyComponent/AddRegisterPemilik";
import AddRegisterStaff from "./RegisterPharmacyComponent/AddRegisterStaff";
import AddRegisterFinalPreview from "./RegisterPharmacyComponent/AddRegisterFinalPreview";
import AddRegisterAccount from "./RegisterPharmacyComponent/AddRegisterAccount";
import ErrorPopup from "../../../common/particles.jsx/ErrorPopup";
import { useNavigate } from "react-router-dom";

const RegisterPharmacy = () => {
  const navigate = useNavigate();

  const [activeStep, setActiveStep] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSameAddress, setIsSameAddress] = useState(true);

  const { data: dataBusiness, isFetching: isFetchingBusiness } = useQuery({
    queryKey: ["business-field"],
    queryFn: () => getBusinessField(),
    refetchOnWindowFocus: false,
  });

  const { data: dataBank, isFetching: isFetchingBank } = useQuery({
    queryKey: ["bank-list"],
    queryFn: () => getBank(),
    refetchOnWindowFocus: false,
  });

  const {
    mutate: mutateCheckStatusByEmail,
    data: dataStatusEmail,
    isPending: isPendingStatusEmail,
  } = useMutation({
    mutationFn: checkStatusByEmail,
  });

  const { data: dataTitle, isFetching: isFetchingTitle } = useQuery({
    queryKey: ["title"],
    queryFn: () => getTitle(),
    refetchOnWindowFocus: false,
  });

  const { data: dataRole, isFetching: isFetchingRole } = useQuery({
    queryKey: ["role"],
    queryFn: () => getRole(),
    refetchOnWindowFocus: false,
  });

  const { mutate: mutateRegister, isPending: isPendingRegister } = useMutation({
    mutationFn: registerNonMpi,
    onSuccess: () => navigate(`/login`),
    onError: (err) =>
      setErrorMessage(err.message.id ? err.message.id : err.message),
  });

  const handleNext = () => {
    var tempMessage = "";
    if (activeStep === 2 || activeStep === 3) {
      if (formik.values.staff.length === 0)
        tempMessage = "Wajib menambahkan 1 staff";
      else if (
        formik.values.staff.filter((staff) => staff.title_id === 3).length === 0
      )
        tempMessage = "Wajib menambahkan 1 Apoteker Penanggung Jawab";
    }
    if (activeStep === 1 || activeStep === 3) {
      if (formik.values.owner.name.length === 0)
        tempMessage = "Nama Pemilik wajib diisi";
      else if (
        formik.values.owner.id.length === 0 &&
        formik.values.owner.id.length < 16
      )
        tempMessage = "No. KTP wajib diisi dengan no. KTP yang valid";
      else if (
        formik.values.owner.phone === 0 ||
        formik.values.owner.phone[0] !== "8" ||
        formik.values.owner.phone.length > 15
      )
        tempMessage = "No. HP wajib diisi dengan nomor yang valid";
      else if (
        formik.values.owner.npwp.length === 0 ||
        formik.values.owner.npwp.length < 15
      )
        tempMessage = "NPWP wajib diisi dengan nomor yang valid";
      else if (
        formik.values.owner.email.length === 0 ||
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formik.values.owner.email)
      )
        tempMessage = "Email wajib diisi dengan email yang valid";
      else if (
        formik.values.owner.files.ktp === "" ||
        formik.values.owner.files.npwp === ""
      )
        tempMessage = "Wajib mengupload semua dokumen";
    }
    if (activeStep === 0 || activeStep === 3) {
      if (formik.values.company.business_field_id === 0)
        tempMessage = "Bidang usaha wajib diisi";
      else if (formik.values.company.name === "")
        tempMessage = "Nama usaha wajib diisi";
      else if (
        formik.values.company.npwp.number.length === 0 ||
        formik.values.company.npwp.number.length < 15
      )
        tempMessage = "NPWP wajib diisi dengan nomor yang valid";
      else if (
        formik.values.company.npwp.address === "" ||
        formik.values.company.npwp.npwp_geography_id === 0
      )
        tempMessage = "Alamat NPWP harus lengkap";
      else if (
        formik.values.company.business.address === "" ||
        formik.values.company.business.business_geography_id === 0
      )
        tempMessage = "Alamat Usaha harus lengkap";
      else if (
        formik.values.company.phone.length === 0 ||
        formik.values.company.phone[0] !== "8"
      )
        tempMessage = "No. HP wajib diisi dan valid";
      else if (
        formik.values.company.nib.length === 0 ||
        formik.values.company.nib.length < 13
      )
        tempMessage = "No. induk berusaha wajib diisi";
      else if (formik.values.company.sipnap_code.length === 0)
        tempMessage = "Kode SIPNAP wajib diisi dengan kode yang valid";
      else if (formik.values.company.permission_letter_id.length === 0)
        tempMessage = "Surat izin usaha wajib diisi";
      else if (formik.values.company.permission_letter_expired === "")
        tempMessage = "Masa berlaku surat izin usaha wajib diisi";
      else if (formik.values.company.bank_account_number === "")
        tempMessage = "Nomor rekening bank wajib diisi";
      else if (formik.values.company.bank === "")
        tempMessage = "Bank wajib diisi";
      else if (formik.values.company.bank_account_name === "")
        tempMessage = "Nama pemilik rekening wajib diisi";
      else if (
        formik.values.company.files.nib === "" ||
        formik.values.company.files.izin_sarana === "" ||
        formik.values.company.files.stempel_sarana === "" ||
        formik.values.company.files.npwp === ""
      )
        tempMessage = "Wajib mengupload semua dokumen";
    }
    if (activeStep === 4) {
      if (formik.values.owner.email === "") tempMessage = "Email wajib diisi";
      if (
        !/^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$/.test(
          formik.values.password
        )
      )
        tempMessage = "Password belum sesuai";
    }

    if (tempMessage !== "") {
      setErrorMessage(tempMessage);
      return;
    }

    if (activeStep === 4 && tempMessage === "")
      mutateRegister({
        ...formik.values,
        email: formik.values.owner.email,
        company: {
          ...formik.values.company,
          npwp: {
            number: formik.values.company.npwp.number,
            address: formik.values.company.npwp.address,
            npwp_geography_id: formik.values.company.npwp.npwp_geography_id,
          },
          business: {
            address: formik.values.company.business.address,
            business_geography_id:
              formik.values.company.business.business_geography_id,
          },
        },
      });
    else setActiveStep(activeStep + 1);
  };

  const formik = useFormik({
    initialValues: {
      type: true,
      email: "",
      password: "",
      company: {
        business_field_id: 0,
        name: "",
        npwp: {
          number: "",
          address: "",
          province: "",
          city: "",
          district: "",
          subdistrict: "",
          postalcode: "",
          npwp_geography_id: 0,
        },
        business: {
          address: "",
          province: "",
          city: "",
          district: "",
          subdistrict: "",
          postalcode: "",
          business_geography_id: 0,
        },
        phone: "",
        nib: "",
        sipnap_code: "",
        permission_letter_id: "",
        permission_letter_expired: "",
        bank: "",
        bank_account_name: "",
        bank_account_number: "",
        files: {
          ktp: "",
          nib: "",
          npwp: "",
          type_ktp: "",
          type_nib: "",
          type_npwp: "",
          izin_sarana: "",
          stempel_sarana: "",
          type_izin_sarana: "",
          spesimen_signature: "",
          type_stempel_sarana: "",
          type_spesimen_signature: "",
        },
      },
      owner: {
        name: "",
        id: "",
        phone: "",
        npwp: "",
        email: "",
        files: {
          type_ktp: "",
          ktp: "",
          npwp: "",
          type_npwp: "",
        },
      },
      staff: [],
      enterprise: {
        enterprise_name: "",
        is_enterprise: false,
      },
    },
    enableReinitialize: true,
  });

  // Checking email
  const debounceEmail = useDebounce(formik.values.owner.email, 500);

  useEffect(() => {
    mutateCheckStatusByEmail({ email: debounceEmail });
  }, [debounceEmail]);

  return (
    <ThemeProvider theme={theme}>
      <div className="text-[12px] text-[#2C3E5D] w-full h-screen">
        <LoadingBackdrop isLoading={false} />
        <div className="md:flex items-stretch min-h-screen w-full">
          <div className="overflow-y-hidden w-full basis-1/3 h-[150px] md:h-screen shadow-sm flex flex-col items-center justify-center p-2 pb-0 gap-4 relative">
            <div className="absolute top-0 h-full flex flex-col justify-center">
              <div className="flex items-center justify-center gap-4">
                <img
                  src={Logo}
                  className="w-[80px] h-[80px] lg:w-[130px] lg:h-[130px] object-contain"
                />
                <p className="font-bold text-[16px]">
                  Registrasi Akun <br /> Non Relasi MPI
                </p>
              </div>
              <p className="text-center pb-4">
                Sudah memiliki akun OLIN?{" "}
                <a href="/login" className="text-blue-400 underline">
                  Login
                </a>
              </p>
            </div>
          </div>

          <div className="w-full bg-gray-100 min-h-screen overflow-y-auto">
            <div className="w-full overflow-x-auto mb-4 pt-10">
              <RegisterPharmacyStepper activeStep={activeStep} />
            </div>

            <div className="mb-4 p-4 pb-0">
              {activeStep === 0 ? (
                <AddRegisterSarana
                  formik={formik}
                  dataBank={dataBank}
                  dataBusiness={dataBusiness}
                  isFetchingBank={isFetchingBank}
                  isFetchingBusiness={isFetchingBusiness}
                  isSameAddress={isSameAddress}
                  setIsSameAddress={setIsSameAddress}
                />
              ) : activeStep === 1 ? (
                <AddRegisterPemilik
                  formik={formik}
                  dataStatusEmail={dataStatusEmail}
                  isPendingStatusEmail={isPendingStatusEmail}
                />
              ) : activeStep === 2 ? (
                <AddRegisterStaff
                  formik={formik}
                  dataTitle={dataTitle}
                  isFetchingTitle={isFetchingTitle}
                  dataRole={dataRole}
                  isFetchingRole={isFetchingRole}
                />
              ) : activeStep === 3 ? (
                <AddRegisterFinalPreview
                  formik={formik}
                  dataTitle={dataTitle}
                  isFetchingTitle={isFetchingTitle}
                  dataRole={dataRole}
                  isFetchingRole={isFetchingRole}
                  dataBank={dataBank}
                  dataBusiness={dataBusiness}
                  isFetchingBank={isFetchingBank}
                  isFetchingBusiness={isFetchingBusiness}
                  setActiveStep={setActiveStep}
                />
              ) : activeStep === 4 ? (
                <AddRegisterAccount formik={formik} />
              ) : null}
            </div>

            <div className="p-4 pt-0">
              <button
                type="button"
                className=" border-2 border-gray-300 p-2 !rounded-md hover:!bg-gray-100 mb-2 w-full disabled:!bg-gray-300 disabled:text-white bg-white"
                onClick={() => setActiveStep(activeStep - 1)}
                disabled={activeStep === 0}
              >
                Back
              </button>
              <button
                type="button"
                className=" bg-blue-500 p-2 !rounded-md text-white hover:bg-gray-300 mb-2 w-full disabled:bg-gray-300"
                onClick={handleNext}
                disabled={
                  isPendingRegister ||
                  isPendingStatusEmail ||
                  (formik.values.owner.email !== "" && !dataStatusEmail)
                }
              >
                {isPendingRegister ? (
                  <CircularProgress size={10} />
                ) : activeStep === 4 ? (
                  "Registrasi Akun"
                ) : (
                  "Selanjutnya"
                )}
              </button>
            </div>
          </div>
        </div>

        <ErrorPopup
          errorMessage={errorMessage}
          setErrorMessage={setErrorMessage}
        />
      </div>
    </ThemeProvider>
  );
};

export default RegisterPharmacy;
